// src/styles/theme.ts
export const theme = {
  colors: {
    primary: '#0A1A41',        // Rich deep blue for primary elements
    secondary: '#4D6DE3',      // Royal blue for secondary elements (replacing red)
    accent: '#16D3A9',         // Teal for accents and highlights (replacing neon)
    tertiary: '#8C43FF',       // Refined purple for tertiary elements
    success: '#00C781',        // Bright green for success states
    warning: '#FFCA58',        // Bright yellow for warnings
    error: '#FF4040',          // Bright red for errors
    dark: '#151C38',           // Dark blue-black for text
    light: '#F8F9FE',          // Very light blue-white for backgrounds
    white: '#FFFFFF',          // Pure white
    grey: {
      100: '#F5F7FF',
      200: '#E6E9F4',
      300: '#D5D9E8',
      400: '#B3B9CC',
      500: '#8C92A8',
      600: '#666D85',
      700: '#464B5D',
      800: '#2A2E3C',
      900: '#171A24',
    },
    gradient: {
      primary: 'linear-gradient(135deg, #0A1A41 0%, #1E3A8A 100%)',
      secondary: 'linear-gradient(135deg, #4D6DE3 0%, #3B5BD9 100%)',
      accent: 'linear-gradient(135deg, #16D3A9 0%, #0BA889 100%)',
      tertiary: 'linear-gradient(135deg, #8C43FF 0%, #6E35B1 100%)',
      neon: 'linear-gradient(135deg, #16D3A9 0%, #8C43FF 100%)',
      hot: 'linear-gradient(135deg, #4D6DE3 0%, #8C43FF 100%)',
      cool: 'linear-gradient(135deg, #16D3A9 0%, #0A1A41 100%)',
      dark: 'linear-gradient(135deg, #151C38 0%, #0A1A41 100%)',
      glow: 'linear-gradient(135deg, #4D6DE3 0%, #16D3A9 100%)',
    },
  },
  fonts: {
    heading: "'Montserrat', sans-serif",
    body: "'Poppins', sans-serif",
    accent: "'Space Grotesk', sans-serif",
    primary: "'Poppins', sans-serif",
    secondary: "'Montserrat', sans-serif",
  },
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem',  // 36px
    '5xl': '3rem',     // 48px
    '6xl': '3.75rem',  // 60px
    '7xl': '4.5rem',   // 72px
  },
  fontWeights: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  lineHeights: {
    tight: 1.2,
    normal: 1.5,
    loose: 1.8,
  },
  space: {
    xs: '0.25rem',     // 4px
    sm: '0.5rem',      // 8px
    md: '1rem',        // 16px
    lg: '1.5rem',      // 24px
    xl: '2rem',        // 32px
    '2xl': '3rem',     // 48px
    '3xl': '4rem',     // 64px
    '4xl': '6rem',     // 96px
  },
  breakpoints: {
    xs: '320px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    '2xl': '1400px',
  },
  shadows: {
    sm: '0 2px 10px rgba(10, 15, 60, 0.08), 0 0 1px rgba(10, 15, 60, 0.08)',
    md: '0 5px 15px rgba(10, 15, 60, 0.1), 0 0 3px rgba(10, 15, 60, 0.1)',
    lg: '0 10px 25px rgba(10, 15, 60, 0.12), 0 0 5px rgba(10, 15, 60, 0.1)',
    xl: '0 20px 40px rgba(10, 15, 60, 0.15), 0 0 10px rgba(10, 15, 60, 0.1)',
    neon: '0 0 10px rgba(0, 245, 212, 0.5), 0 0 20px rgba(0, 245, 212, 0.3)',
    glow: '0 0 15px rgba(255, 51, 102, 0.5), 0 0 30px rgba(255, 51, 102, 0.2)',
    inner: 'inset 0 2px 4px rgba(10, 15, 60, 0.1)',
  },
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '24px',
    pill: '50px',
    full: '9999px',
    blob: '60% 40% 40% 60% / 60% 30% 70% 40%',
    fancy: '10px 40px 10px 40px',
  },
  transitions: {
    fast: '0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    normal: '0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    slow: '0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    bounce: '0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    elastic: '0.6s cubic-bezier(0.68, -0.6, 0.32, 1.6)',
  },
  animations: {
    fadeIn: 'fadeIn 0.5s ease-in-out',
    slideUp: 'slideUp 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    slideDown: 'slideDown 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    slideLeft: 'slideLeft 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    slideRight: 'slideRight 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    pulse: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    float: 'float 3s ease-in-out infinite',
    bounce: 'bounce 1s ease infinite',
    spin: 'spin 1s linear infinite',
    glow: 'glow 1.5s ease-in-out infinite alternate',
  },
  zIndices: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    fixed: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    tooltip: 1600,
  },
};

export type Theme = typeof theme;
